// The shell mark's catalog: geometry, identity vocabulary, state colour and the loop keyframes.
// Pure data and pure functions, no React. Every path is authored in one 64×64 box, so shells, eyes,
// gear and the fold plane share one coordinate space.
//
// Source: the team's "marketplace foundry" design. Identity (shell, proportion, eyes, fold, hue)
// answers "who?", and state (expression, colour, face loops) answers "what now?".
// Static expressions live in `globals.css` (`[data-agent-avatar-mark="shell"]`); the loops are Web
// Animations started by `AgentShellAvatar`, never CSS animations (`pixel-agent-mark:cel-strip-stepped-animation`).

import { type AgentAvatarState } from './agent-avatar-state';

/** Shell topology: the primary identity channel. */
export const AGENT_SHELLS = [
	'tile',
	'arch',
	'wedge',
	'shield',
	'pebble',
	'ticket',
	'step',
	'shoulder',
	'bell',
	'gem',
	'cove',
	'link',
] as const;
export type AgentShell = (typeof AGENT_SHELLS)[number];

export const AGENT_SHELL_PATHS: Record<AgentShell, string> = {
	tile: 'M20 3H48C56 3 61 8 61 16V44C61 55 55 61 44 61H20C9 61 3 55 3 44V20C3 9 9 3 20 3Z',
	arch: 'M32 2C48 2 60 14 60 30V48C60 56 55 61 47 61H17C9 61 4 56 4 48V30C4 14 16 2 32 2Z',
	wedge: 'M14 7L50 2C57 1 61 7 60 15L56 48C55 57 49 61 41 61H17C8 61 3 54 5 45L9 18C10 12 11 8 14 7Z',
	shield: 'M16 3H48C56 3 61 9 60 17L57 40C56 50 49 57 32 62C15 57 8 50 7 40L4 17C3 9 8 3 16 3Z',
	pebble: 'M25 2C40 0 55 5 60 18C64 31 59 51 46 58C33 65 11 61 5 47C-1 34 3 15 14 7C17 5 21 3 25 2Z',
	ticket: 'M18 3H49C56 3 61 8 61 15V49C61 56 56 61 49 61H18C10 61 4 55 4 48C12 45 12 39 4 36V18C4 9 10 3 18 3Z',
	step: 'M18 3H49C56 3 61 8 61 15V49C61 56 56 61 49 61H15C8 61 3 56 3 49V24H11V13H18V3Z',
	shoulder:
		'M19 3H45C50 3 52 7 53 12C58 13 61 18 61 24V46C61 55 55 61 46 61H18C9 61 3 55 3 46V24C3 18 6 13 11 12C12 7 14 3 19 3Z',
	bell: 'M18 3H46C52 3 55 7 56 13L61 48C62 56 56 61 49 61H15C8 61 2 56 3 48L8 13C9 7 12 3 18 3Z',
	gem: 'M16 3H49L61 15V49L49 61H15L3 49V16L16 3Z',
	cove: 'M20 3H49C56 3 61 8 61 15V49C61 56 56 61 49 61H17C9 61 3 55 3 47V27C11 25 13 18 6 13C9 6 14 3 20 3Z',
	link: 'M18 3H46C55 3 61 10 60 19C53 24 53 40 60 45C60 55 54 61 45 61H19C10 61 4 55 4 45C11 40 11 24 4 19C3 10 9 3 18 3Z',
};

/**
 * Body proportion. A pure scale about the box centre, so one path serves all three. The design
 * calls these "profiles"; "proportion" keeps the word apart from the agent profile page.
 */
export const AGENT_PROPORTIONS = ['tall', 'balanced', 'wide'] as const;
export type AgentProportion = (typeof AGENT_PROPORTIONS)[number];

type ProportionSpec = {
	/**
	 * SVG `transform` attribute for the shell group. It centres itself on (32, 32), so the group
	 * must NOT also get a CSS `transform-origin`: that centres it a second time. The design page does
	 * exactly that and draws tall bodies 3.8 units right of their eyes (wide ones 3.8 low).
	 */
	readonly transform: string;
	/** Where the fold plane's diagonal meets the box edges (see `foldPlanePath`). */
	readonly foldStart: number;
	readonly foldEnd: number;
};

export const AGENT_PROPORTION_SPECS: Record<AgentProportion, ProportionSpec> = {
	tall: {
		transform: 'translate(32 32) scale(.88 1) translate(-32 -32)',
		foldStart: 32,
		foldEnd: 28,
	},
	balanced: {
		transform: 'translate(32 32) scale(1 1) translate(-32 -32)',
		foldStart: 31,
		foldEnd: 27,
	},
	wide: {
		transform: 'translate(32 32) scale(1 .88) translate(-32 -32)',
		foldStart: 29,
		foldEnd: 25,
	},
};

/** Which corner carries the gold fold. */
export const AGENT_FOLD_CORNERS = ['top-left', 'top-right', 'bottom-left', 'bottom-right'] as const;
export type AgentFoldCorner = (typeof AGENT_FOLD_CORNERS)[number];

/**
 * The gold plane is `shell ∩ corner wedge`, never a shape of its own: the fold is the SAME shell
 * path clipped to this triangle. That is why it can never break a silhouette, and why a new shell
 * needs no new gold artwork.
 *
 * Do not replace this with a drawn triangle — it would escape concave shells (`step`, `cove`,
 * `ticket`) at their notches.
 */
export function foldPlanePath(proportion: AgentProportion, corner: AgentFoldCorner): string {
	const { foldStart, foldEnd } = AGENT_PROPORTION_SPECS[proportion];
	const mirroredStart = 64 - foldStart;
	const mirroredEnd = 64 - foldEnd;
	switch (corner) {
		case 'top-left':
			return `M${mirroredStart} -8H-12V${mirroredEnd}Z`;
		case 'top-right':
			return `M${foldStart} -8H76V${mirroredEnd}Z`;
		case 'bottom-left':
			return `M${mirroredStart} 72H-12V${foldEnd}Z`;
		case 'bottom-right':
			return `M${foldStart} 72H76V${foldEnd}Z`;
		default: {
			const unhandled: never = corner;
			throw new Error(`Unhandled fold corner: ${String(unhandled)}`);
		}
	}
}

/**
 * Eye shape — identity, never state; state moves and squashes these. `mismatched` alternates
 * round and pill eyes, starting with round.
 */
export const AGENT_EYE_SHAPES = ['pill', 'round', 'window', 'almond', 'aperture', 'mismatched'] as const;
export type AgentEyeShape = (typeof AGENT_EYE_SHAPES)[number];

/**
 * Two eyes, always (owner call), at the design's two-eye placement. The uneven expressions
 * (thinking, error) treat the left and right eye differently.
 */
export const AGENT_EYE_CENTERS = [
	{ x: 21.5, y: 30.5, side: 'left' },
	{ x: 42.5, y: 30.5, side: 'right' },
] as const;

/**
 * One eye white, as a renderer-agnostic element descriptor. A descriptor rather than markup keeps
 * this file free of JSX and lets the component render real React children.
 */
export type AgentEyeElement =
	| {
			readonly tag: 'rect';
			readonly x: number;
			readonly y: number;
			readonly width: number;
			readonly height: number;
			readonly rx: number;
	  }
	| { readonly tag: 'circle'; readonly cx: number; readonly cy: number; readonly r: number }
	| { readonly tag: 'path'; readonly d: string };

/** The eye white for the eye at `index` (0 left, 1 right), centred on (cx, cy). */
export function agentEyeElement(shape: AgentEyeShape, index: number, cx: number, cy: number): AgentEyeElement {
	switch (shape) {
		case 'pill':
			return { tag: 'rect', x: cx - 6.5, y: cy - 10.5, width: 13, height: 21, rx: 6.5 };
		case 'round':
			return { tag: 'circle', cx, cy, r: 8.2 };
		case 'window':
			return { tag: 'rect', x: cx - 8.5, y: cy - 6.5, width: 17, height: 13, rx: 6.5 };
		case 'almond':
			return {
				tag: 'path',
				d: `M${cx - 9.5} ${cy}C${cx - 6.5} ${cy - 8.5} ${cx + 6.5} ${cy - 8.5} ${cx + 9.5} ${cy}C${cx + 6.5} ${cy + 8.5} ${cx - 6.5} ${cy + 8.5} ${cx - 9.5} ${cy}Z`,
			};
		case 'aperture':
			return {
				tag: 'path',
				d: `M${cx - 6.5} ${cy - 9.5}H${cx + 3.5}L${cx + 8.5} ${cy}L${cx + 3.5} ${cy + 9.5}H${cx - 6.5}L${cx - 11.5} ${cy}Z`,
			};
		case 'mismatched':
			return agentEyeElement(index % 2 === 1 ? 'pill' : 'round', index, cx, cy);
		default: {
			const unhandled: never = shape;
			throw new Error(`Unhandled eye shape: ${String(unhandled)}`);
		}
	}
}

export const AGENT_PUPIL_RADIUS = 2.8;

/** Smiling-eye arc shown in place of the eye white when the run succeeded. */
export function agentHappyEyePath(cx: number, cy: number): string {
	return `M${cx - 8} ${cy + 4}Q${cx} ${cy - 6} ${cx + 8} ${cy + 4}`;
}

export type AgentShellIdentity = {
	/** 0–360. The agent's own colour: its idle body, name tint and mention chip. */
	readonly hue: number;
	readonly shell: AgentShell;
	readonly proportion: AgentProportion;
	readonly eyeShape: AgentEyeShape;
	readonly foldCorner: AgentFoldCorner;
};

/** The idle hue the design authors every state tone for (its teal reference agent). */
const DESIGN_REFERENCE_HUE = 201;

type Tone = { readonly l: number; readonly c: number; readonly h: number };

const IDLE_TONE: Tone = { l: 0.48, c: 0.105, h: 201 };
const WAITING_TONE: Tone = { l: 0.63, c: 0.15, h: 65 };
const THINKING_TONE: Tone = { l: 0.52, c: 0.145, h: 303 };

/**
 * The design's state tones for its reference agent. Lightness and chroma are what the design
 * renders after its own clamp (chroma ≤ 0.155, so working and error lose a little). The three
 * states the design does not draw take the nearest one (owner call): `queued` rests in the idle
 * tone, `recovering` thinks in the waiting tone, and `stopped` is the idle hue faded to grey.
 */
const AGENT_SHELL_TONES: Record<AgentAvatarState, Tone> = {
	idle: IDLE_TONE,
	queued: IDLE_TONE,
	thinking: THINKING_TONE,
	working: { l: 0.53, c: 0.155, h: 254 },
	waiting: WAITING_TONE,
	recovering: WAITING_TONE,
	success: { l: 0.51, c: 0.135, h: 151 },
	error: { l: 0.54, c: 0.155, h: 27 },
	// Not in the design: a faded grey that keeps a trace of the agent's hue.
	paused: IDLE_TONE,
	stopped: { l: 0.55, c: 0.03, h: 201 },
};

/**
 * The body colour for `state`, rotated onto the agent's own hue: each design tone turns by
 * `hue - 201`, so idle IS the agent's colour and every other state is a whole-body shift away from
 * it that keeps the design's spacing between tones.
 *
 * It is not a traffic light. The same state lands on a different colour for each agent (waiting is
 * amber only for an agent whose idle hue is the reference teal); the face says which state, the
 * colour says that the agent moved.
 */
export function agentShellFill(state: AgentAvatarState, hue: number): string {
	const { l, c, h } = AGENT_SHELL_TONES[state];
	const turned = (((hue + h - DESIGN_REFERENCE_HUE) % 360) + 360) % 360;
	return `oklch(${l} ${c} ${turned.toFixed(1)})`;
}

/** One line on what the shell mark does per state, kept next to the data that draws it. */
export const AGENT_SHELL_STATE_NOTES: Record<AgentAvatarState, string> = {
	idle: "Open eyes, centred gaze, resting posture, in the agent's own colour.",
	queued: 'Half-lidded, glancing up, still in its own colour: accepted, not started.',
	thinking: 'Head tipped, eyes uneven and looking up, trading size as it thinks.',
	working: 'Narrowed eyes, pupils scanning the task.',
	waiting: 'Eyes pressed to a low bar, holding still: a person is the blocker.',
	recovering: 'The thinking posture in the waiting tone: still running, finding its feet.',
	success: 'Smiling arcs for eyes, a small lift and a tilt.',
	error: 'Slanted, concerned eyes and a contracted posture.',
	paused: 'Asleep, eyes shut; retains the agent’s identity colour.',
	stopped: 'Eyes shut to a short dash, the body faded to grey.',
};

/**
 * Which part of the face a loop moves. Loops move the face only, never the body: the owner found
 * the design's body wobble and temperament motion too shaky. The component maps each target to its
 * element(s).
 */
export const AGENT_SHELL_LOOP_TARGETS = ['left-eye', 'right-eye', 'pupil'] as const;
export type AgentShellLoopTarget = (typeof AGENT_SHELL_LOOP_TARGETS)[number];

/** One infinite loop, in Web Animations terms. */
export type AgentShellLoop = {
	readonly target: AgentShellLoopTarget;
	readonly keyframes: readonly Keyframe[];
	readonly duration: number;
};

/**
 * CSS-style keyframe stops — `[[0, 0.18, 1], "scale(1)"]` — expanded to Web Animations keyframes
 * sorted by offset. `easing` sits on every keyframe, because a CSS `animation-timing-function`
 * eases each segment, while a WAAPI `easing` option eases the whole iteration.
 */
function stops(easing: string, ...entries: readonly (readonly [readonly number[], string])[]) {
	return entries
		.flatMap(([offsets, transform]) => offsets.map((offset) => ({ offset, transform, easing })))
		.sort((a, b) => a.offset - b.offset);
}

const DESIGN_EASE = 'cubic-bezier(.45, 0, .25, 1)';

/** Thinking: the eyes trade size and the gaze wanders up, from the design (2.8s). */
const THINKING_LOOPS: readonly AgentShellLoop[] = [
	{
		target: 'left-eye',
		keyframes: stops(
			DESIGN_EASE,
			[[0, 0.18, 1], 'translate(-1px, -3px) scale(1.15, 1.12)'],
			[[0.4, 0.54], 'translate(-1px, 1px) scale(.82, .84)'],
			[[0.72, 0.84], 'translate(-1px, -2px) scale(1.06, 1.04)'],
		),
		duration: 2800,
	},
	{
		target: 'right-eye',
		keyframes: stops(
			DESIGN_EASE,
			[[0, 0.18, 1], 'translate(1px, 1px) scale(.78, .8)'],
			[[0.4, 0.54], 'translate(1px, -3px) scale(1.15, 1.12)'],
			[[0.72, 0.84], 'translate(1px, -1px) scale(.94, .96)'],
		),
		duration: 2800,
	},
	{
		target: 'pupil',
		keyframes: stops(
			DESIGN_EASE,
			[[0, 0.18, 1], 'translate(2px, -4px)'],
			[[0.4, 0.54], 'translate(-2px, -3px)'],
			[[0.72, 0.84], 'translate(0, -1px)'],
		),
		duration: 2800,
	},
];

/** Working: the pupils scan side to side, from the design (900ms). */
const WORKING_LOOPS: readonly AgentShellLoop[] = [
	{
		target: 'pupil',
		keyframes: stops('ease-in-out', [[0, 1], 'translateX(-2px)'], [[0.5], 'translateX(3px)']),
		duration: 900,
	},
];

/**
 * The loops a state plays. Only running states loop — thinking, working, and recovering (which
 * thinks) — and only the face moves. Resting and terminal states hold their pose, so a mostly idle
 * list is still. Exhaustive: a new state fails typecheck here.
 */
export function agentShellLoops(state: AgentAvatarState): readonly AgentShellLoop[] {
	switch (state) {
		case 'thinking':
		case 'recovering':
			return THINKING_LOOPS;
		case 'working':
			return WORKING_LOOPS;
		case 'idle':
		case 'queued':
		case 'waiting':
		case 'success':
		case 'error':
		case 'paused':
		case 'stopped':
			return [];
		default: {
			const unhandled: never = state;
			throw new Error(`Unhandled avatar state for shell loops: ${String(unhandled)}`);
		}
	}
}
